import React, { useEffect, useRef, useState } from "react";

/**
 * Fundo animado de página inteira do dashboard — inspirado no estilo
 * "Diet Soda" (gradiente radial escuro + bolhas + elementos centrais
 * flutuantes reagindo ao cursor), adaptado para mostrar as marcas do
 * ecossistema AutoPostBR em vez de um produto 3D.
 *
 * Renderizado como `position: fixed` cobrindo toda a área de conteúdo
 * (atrás do conteúdo da página, que deve usar `position: relative` e
 * cores claras por cima).
 */

const LOGOS = [
  { id: "autopost", src: "/logo-autopostbr.png", alt: "AutoPost BR", size: 180, top: "46%", left: "50%", isCenter: true },
  { id: "whatsapp", src: "/whatsapp.png", alt: "WhatsApp", size: 110, top: "22%", left: "22%" },
  { id: "facebook", src: "/facebook.png", alt: "Facebook", size: 100, top: "24%", left: "78%" },
  { id: "instagram", src: "/instagram.png", alt: "Instagram", size: 104, top: "76%", left: "68%" },
];

export default function DashboardHeroBackground() {
  const bubblesRef = useRef(null);
  const logoRefs = useRef({});
  const mouse = useRef({ px: -9999, py: -9999 });
  const [ready, setReady] = useState(false);

  // Loop de animação: flutuação + repulsão por cursor
  useEffect(() => {
    let raf;
    const state = {};
    LOGOS.forEach((l) => {
      state[l.id] = { rx: 0, ry: 0 };
    });

    const animate = (t) => {
      const time = t * 0.001;
      LOGOS.forEach((l, i) => {
        const el = logoRefs.current[l.id];
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const diffX = mouse.current.px - cx;
        const diffY = mouse.current.py - cy;
        const dist = Math.sqrt(diffX * diffX + diffY * diffY);

        let targetRx = 0;
        let targetRy = 0;
        const radius = l.isCenter ? 320 : 240;
        const strength = l.isCenter ? -26 : -50;
        if (dist < radius && dist > 0) {
          const force = (radius - dist) / radius;
          targetRx = (diffX / dist) * force * strength;
          targetRy = (diffY / dist) * force * strength;
        }

        const s = state[l.id];
        s.rx += (targetRx - s.rx) * 0.08;
        s.ry += (targetRy - s.ry) * 0.08;

        const dur = [7, 6, 8, 6.5][i % 4];
        const phase = (time + i * 0.9) * ((Math.PI * 2) / dur);
        const floatY = Math.sin(phase) * (l.isCenter ? 14 : 18);
        const floatAngle = Math.cos(phase) * (l.isCenter ? 3 : 6);

        el.style.transform = `translate(-50%, -50%) translate(${s.rx}px, ${s.ry + floatY}px) rotate(${floatAngle}deg)`;
      });
      raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);

    const onMove = (e) => {
      mouse.current.px = e.clientX;
      mouse.current.py = e.clientY;
    };
    window.addEventListener("mousemove", onMove);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  // Gerador de bolhas
  useEffect(() => {
    const container = bubblesRef.current;
    if (!container) return;
    const interval = setInterval(() => {
      const bubble = document.createElement("span");
      const size = Math.random() * 18 + 8;
      bubble.style.position = "absolute";
      bubble.style.bottom = "-30px";
      bubble.style.left = Math.random() * 100 + "%";
      bubble.style.width = size + "px";
      bubble.style.height = size + "px";
      bubble.style.borderRadius = "50%";
      bubble.style.background = "rgba(255,255,255,0.10)";
      bubble.style.border = "1px solid rgba(255,255,255,0.16)";
      bubble.style.pointerEvents = "none";
      const duration = Math.random() * 5 + 5;
      bubble.style.animation = `dashboardBubbleUp ${duration}s linear forwards`;
      container.appendChild(bubble);
      setTimeout(() => bubble.remove(), duration * 1000);
    }, 420);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => setReady(true), []);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        overflow: "hidden",
        background: "radial-gradient(circle at 50% 40%, #0b8a78 0%, #044e3b 45%, #011411 85%)",
        pointerEvents: "none",
      }}
      data-testid="dashboard-hero-background"
    >
      <style>{`
        @keyframes dashboardBubbleUp {
          0% { transform: translateY(0); opacity: 0; }
          10% { opacity: 0.45; }
          90% { opacity: 0.45; }
          100% { transform: translateY(-110vh); opacity: 0; }
        }
        @keyframes dashboardFadeIn {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.85); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>

      {/* Bolhas */}
      <div ref={bubblesRef} style={{ position: "absolute", inset: 0, overflow: "hidden" }} />

      {/* Logos flutuantes */}
      {LOGOS.map((l, i) => (
        <img
          key={l.id}
          ref={(el) => (logoRefs.current[l.id] = el)}
          src={l.src}
          alt={l.alt}
          style={{
            position: "absolute",
            top: l.top,
            left: l.left,
            width: l.size,
            height: l.size,
            objectFit: "contain",
            transform: "translate(-50%, -50%)",
            filter: l.isCenter
              ? "drop-shadow(0 16px 40px rgba(0,0,0,0.5))"
              : "drop-shadow(0 10px 24px rgba(0,0,0,0.4))",
            opacity: ready ? 1 : 0,
            animation: ready ? `dashboardFadeIn 0.9s ease-out ${i * 0.12}s backwards` : "none",
          }}
        />
      ))}
    </div>
  );
}
